import { MapContainer, TileLayer, Marker, Popup, Tooltip, CircleMarker, Circle } from 'react-leaflet'
import * as L from "leaflet"
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetLocationsQuery } from 'features/core/map/api/locationsApiSlice';
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import Loading from 'components/commons/Loading';
import getSafetyCardColor, { getSafetyIcon, SafetyStatusBadge } from '../SafetyUtils';
import secondsToHms from 'utility/SecondToHms';

export default function SafetyMapDialog({ focusAlarm }) {
    const [t] = useTranslation(["global", "safety"]);
    const [openDialog, setOpenDialog] = useState(false);


    // API
    const {
        data,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch,
    } = useGetLocationsQuery()

    let content
    let minimumLocations


    const defaultLat = 41.902782
    const defaultLong = 12.496366
    const starterZoom = 8 //12 ok
    const maxZoom = 20

    var markerSize = { 'sm': [28, 35], 'md': [35, 44], 'lg': [42, 53] };
    var markerAnchor = { 'sm': [14, 35], 'md': [17, 44], 'lg': [21, 53] };
    var markerPopupAnchor = { 'sm': [1, -35], 'md': [1, -44], 'lg': [2, -53] };

    //  Create the Icon
    const LeafIcon = L.Icon.extend({
        options: {}
    });

    const blueIcon = new LeafIcon({
        iconUrl:
            "https://assets.mapquestapi.com/icon/v2/marker-B@2x.png"
    })

    function getMarkerIcon(location) {

        let pinBackgroundColor

        switch (location.type) {
            case "BUSINESS_UNIT":
                pinBackgroundColor = "2bb673"
                break
            case "ALARM_SOURCE":
                pinBackgroundColor = "red"
                break
            default: pinBackgroundColor = "2bb673"
        }

        return new LeafIcon({
            iconUrl: `https://assets.mapquestapi.com/icon/v2/marker-lg-2c363c-${pinBackgroundColor}-${location.name}.png`,
            iconSize: markerSize.lg,
            iconAnchor: markerAnchor.lg,
            popupAnchor: markerPopupAnchor.lg
        })
    }

    function getAlarmIcon(alarm) {
        return new LeafIcon({
            iconUrl: `https://assets.mapquestapi.com/icon/v2/marker-lg-2c363c-red-${alarm.user.displayName}.png`,
            iconSize: markerSize.lg,
            iconAnchor: markerAnchor.lg,
            popupAnchor: markerPopupAnchor.lg
        })
    }

    function getDestination(location) {
        switch (location.type) {
            case "BUSINESS_UNIT": return "/dashboard/businessUnits/detail"
            case "ALARM_SOURCE": return "/dashboard/safety/dashboard"
            default: return
        }
    }

    function abbreviateName(displayName) {
        // Rimuove spazi extra e divide nome e cognome
        const parti = displayName.trim().split(/\s+/);

        if (parti.length < 2) {
            // Se c'è solo una parola, restituisce così com'è
            return displayName;
        }

        // Prende la prima lettera del nome e tutto il resto come cognome
        const firstname = parti[0];
        const surname = parti.slice(1).join(' ');

        return `${firstname.charAt(0).toUpperCase()}. ${surname.charAt(0).toUpperCase()}`;
    }

    if (isSuccess) {

        minimumLocations = data.length > 0


        const mapBounds = [
            [Math.max(...data.map(l => l.latitude)), Math.min(...data.map(l => l.longitude))],
            [Math.min(...data.map(l => l.latitude)), Math.max(...data.map(l => l.longitude))]
        ]

        content = <div className=" px-4 mt-4 row">
            {
                focusAlarm.position != null &&
                <div className='col-auto'>
                    <div className="card bg-dark text-white mb-4">
                        <div className="card-header">
                            {getSafetyIcon(focusAlarm)} {t(`safety:entity.alarm.type.${focusAlarm.type}`)}
                        </div>
                        <div className={`card-body ${getSafetyCardColor(focusAlarm)}`}>
                            <h5>{focusAlarm.user.displayName}</h5>
                            <SafetyStatusBadge alarm={focusAlarm} />
                        </div>
                        <div className='card-footer'>
                            <small className="text-muted">{new Date(focusAlarm.epochTime * 1000).toLocaleDateString("it-IT", { hour: "2-digit", minute: "2-digit" })}</small>
                        </div>
                    </div>

                    <div className="card bg-dark text-white mb-4">
                        <div className="card-header">
                            <i className="fa-solid fa-location-dot"></i> {t("property.location")}
                        </div>
                        <div className="card-body">
                            <h5> GPS</h5>
                            <table>
                                <tbody>
                                    <tr>
                                        <td><b className="text-eh">Lat</b></td>
                                        <td>{focusAlarm.position.latitude}</td>
                                    </tr>
                                    <tr>
                                        <td><b className="text-eh">Long</b></td>
                                        <td>{focusAlarm.position.longitude}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className='card-footer'>
                            <small className="text-muted">{new Date(focusAlarm.position.epochSeconds * 1000).toLocaleDateString("it-IT", { hour: "2-digit", minute: "2-digit" })}</small>
                        </div>
                    </div>

                    <small className="text-muted">  <i className="fa-solid fa-clock"></i> {t("safety:label.loc_time")}: </small>{secondsToHms(focusAlarm.position.epochSeconds - focusAlarm.epochTime)}
                    <br />
                    <small className="text-muted">  <i className="fa-solid fa-crosshairs"></i> {t("safety:label.loc_accuracy")}: </small>{focusAlarm.position.accuracy || 5} m


                </div>

            }
            <div className=" col">
                <MapContainer
                    bounds={minimumLocations ? mapBounds : null}
                    // boundsOptions={{ padding: [100, 100] }}
                    center={focusAlarm ? [focusAlarm.position.latitude, focusAlarm.position.longitude] : [defaultLat, defaultLong]}
                    zoom={focusAlarm ? maxZoom : starterZoom}
                    scrollWheelZoom={true}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={maxZoom} maxNativeZoom={19}
                    />

                    {data && data.map(location => (
                        <Marker position={[location.latitude, location.longitude]} icon={new getMarkerIcon(location)} key={location.guid}>
                            <Tooltip><b>{location.name}</b></Tooltip>
                            <Popup>
                                <small className="text-muted">{[location.code]}</small>
                                <h4>{[location.name]}</h4>
                                <p className="">
                                    {location.description}
                                </p>
                            </Popup>
                        </Marker>

                    ))}

                    {focusAlarm &&
                        <Marker position={[focusAlarm.position.latitude, focusAlarm.position.longitude]} icon={new getAlarmIcon(focusAlarm)} key={focusAlarm.guid}>
                            <Tooltip><b>{focusAlarm.user.displayName}</b></Tooltip>
                            <Popup >
                                <small className="text-muted">{t(`safety:entity.alarm.type.${focusAlarm.type}`).toUpperCase()}</small>
                                <h5>{[focusAlarm.user.displayName]}</h5>
                                <SafetyStatusBadge alarm={focusAlarm} />
                            </Popup>
                        </Marker>

                    }

                    {focusAlarm &&
                        <Circle center={[focusAlarm.position.latitude, focusAlarm.position.longitude]} radius={focusAlarm.position.accuracy || 5} key={focusAlarm.guid} pathOptions={{ color: 'red' }}></Circle>

                    }

                </MapContainer>
            </div>

        </div >
    } else <Loading />

    return (
        <>
            <button className="btn text-light" onClick={() => setOpenDialog(true)}><h3><i className="fa-solid fa-map-location-dot"></i></h3></button>
            <Dialog header={"Safety Map"} visible={openDialog} onHide={() => setOpenDialog(false)} style={{ minWidth: '500px', width: '60vw' }}>
                {content}

            </Dialog>
        </>
    );
};